import type { UIRouterReact } from "@uirouter/react/lib/core";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { clone, find, merge } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { onUnsupportedCountryRedirect } from "app/actions/commonActions";
import { canNavigate } from "app/common/services/canNavigate";
import { chosenItems } from "app/common/services/chosenItems";
import { chosenSites } from "app/common/services/chosenSites";
import { getActiveSibling, getHomePage } from "app/common/services/moduleService";
import { OLD_TO_NEW_CATEGORIES_MAP } from "app/common/services/oldToNewCategoriesMap";
import { getRestrictionsPathForSI } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import { isSubdomainsFilterDisabled } from "app/components/filters-bar/utils";
import { isAppAnalysisPage } from "app/components/sidebar3/primary-side-bar/utils";
import { getHasPremiumPlusClaim } from "app/pages/app-intelligence/helpers";
import { ShareDashboardService } from "app/pages/dashboard/ShareDashboardService";
import {
    FIND_LEADS_SAVED_SEARCH_ROUTE,
    HOME_PAGE_ROUTE,
    COMPANY_ANALYSIS_HOME,
    SI_ANALYSIS_TAB_ROUTES_SET,
    STATIC_LIST_PAGE_ROUTE,
} from "app/pages/sales-intelligence/constants/routes";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { SEGMENTS_ANALYSIS_ABSTRACT_PARENT_ID } from "app/pages/segments/config/const";
import { AssetsService } from "app/services/AssetsService";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { abortAllPendingRequests } from "app/services/fetchService";
import { openStateUnlockModal } from "app/services/ModalService";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import { RecentService } from "app/services/recent/recentService";
import type { ISubscriptionSolution } from "app/user-data/solutionBaseInterfaces";
import {
    getAvailableSolutions,
    getSwitchAccountUrl,
    isSolutionInMyOtherSubscriptions,
    isSolutionInMyUserSubscription,
} from "app/user-data/Solutions";
import type { IRouterState, IRouterStateWithName } from "single-spa/react-app/router/IRouterState";
import {
    isCustomDurationAllowed,
    checkIfNonDynamicParamsChanged,
    resolveFallbackAllowedDuration,
    webSourceNavChangeHandler,
    validateWebsiteKey,
} from "single-spa/react-app/router/NavigationUtils";
import { store } from "single-spa/store/store";

import { proNavigator } from "../ProNavigator";

import { applyResetParams, areParamsEqual } from "./utils/resetParamsUtils";

/**
 * Handles switching the user context if the requested solution is not part of the current user's subscription.
 * - If the solution belongs to the current user, do nothing.
 * - If the solution belongs to another user in a different subscription, abort all other routing,
 *   and call switch account to move to that user context.
 *
 * @param subscriptionSolutions - List of all subscription solutions available across users.
 * @param toState - The target router state.
 */
const handleUserSwitch = (subscriptionSolutions: ISubscriptionSolution[], toState: IRouterState): void => {
    const mySolution = getMyCurrentSolution(toState);
    const myUserId = swSettings.user.id;

    if (isSolutionInMyUserSubscription(subscriptionSolutions, myUserId, mySolution)) {
        return;
    }

    if (isSolutionInMyOtherSubscriptions(subscriptionSolutions, mySolution)) {
        abortAllPendingRequests();
        redirectToSwitchAccount(mySolution);
    }
};

const getMyCurrentSolution = (toState: IRouterState): string => {
    const isAppAnalysis = isAppAnalysisPage(toState.name);
    const isSalesSolution = proNavigator.isSalesSolution(toState);

    if (isAppAnalysis) {
        return "app-solution";
    }
    if (isSalesSolution) {
        return "sales-solution";
    }
    return "web-solution";
};

const redirectToSwitchAccount = (mySolution: string): void => {
    const solutionObj = getAvailableSolutions().find((sol) => sol.itemKey === mySolution);
    if (solutionObj) {
        const returnUrl = window.location.href;
        const switchAccountUrl = getSwitchAccountUrl(solutionObj, returnUrl);
        // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to type 'string' — getSwitchAccountUrl can return null but this branch is only reached when a switch URL is expected
        window.location.href = switchAccountUrl;
    }
};

export const routerOnStart = (router: UIRouterReact) => {
    const swConnectedAccountsService = connectedAccountsServiceFactory(proNavigator);
    router.transitionService.onStart({}, async (transition) => {
        // `name` is narrowed to `string`: the router guarantees a registered state name on transition.to()
        const toState = transition.to() as IRouterStateWithName;
        const toParams = { ...transition.params() };
        const fromState = transition.from() as IRouterState;
        const fromParams = transition.params("from");
        const isSIUser = salesSettingsHelper.isSiUserWithSalesDefaultHomepage();
        const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
        const isQueryLimitReached = isSIUser
            ? await quotaQueryClient.getIsCompanyQueryLimiterEnabled()
            : RecentService.isQueryLimiterEnabled();
        handleUserSwitch(swSettings.subscriptionSolutions, toState);

        // returns the path of the give state with params
        const getStatePath = (stateObj: any, stateParams: any) =>
            new URL(router.stateService.href(stateObj, stateParams, { absolute: true }).replace("/#", "")).pathname;
        // Abort all pending requests when navigating to a different url path
        if (
            toState.name !== fromState.name || // not the same state
            toState.reloadOnSearch !== false || // target state reloads on query search changes
            toState.dynamic === false || // target state reloads on query search changes
            checkIfNonDynamicParamsChanged(toState, fromParams, toParams) ||
            getStatePath(toState, toParams) !== getStatePath(fromState, fromParams) // url path has changed
        ) {
            abortAllPendingRequests();
        }

        /**
         * In case the user has link to old Sales Workspace redirect to SI 2.0 for Sales 2.0 users
         */
        const typePath =
            swSettings.user.hasSI && !swSettings.user.isSimilarWebUser && getRestrictionsPathForSI(toState);

        if (typePath) {
            // evt.preventDefault();
            if (typePath === "home_page") {
                return transition.router.stateService.target(HOME_PAGE_ROUTE, {}, { location: "replace" });
            }
            if (typePath === "workspace") {
                if (!toParams.searchId && !toParams.listId) {
                    return transition.router.stateService.target(HOME_PAGE_ROUTE, {}, { location: "replace" });
                }
                // static list
                if (!toParams.searchId && toParams.listId) {
                    return transition.router.stateService.target(
                        STATIC_LIST_PAGE_ROUTE,
                        {
                            id: toParams.listId,
                        },
                        { location: "replace" },
                    );
                }
                // Saved search result
                if (toParams.searchId) {
                    return transition.router.stateService.target(
                        FIND_LEADS_SAVED_SEARCH_ROUTE,
                        {
                            savedSearchId: toParams.searchId,
                        },
                        { location: "replace" },
                    );
                }
            }
        }

        if (toParams.duration && toParams.duration.indexOf("&") > 0) {
            // eslint:disable-next-line:variable-name
            const _splitDuration = toParams.duration.split("&");
            toParams.duration = _splitDuration[0];
        }
        let newParams;
        let unsupportedDuration;
        let unsupportedCountry;
        let unSupportedOldCategoryNewMapping;
        let unsupportedDomain;
        const modifiedPrams = proNavigator.validateParams(toParams, toState);
        /* restrict # of apps to be less than 6 */
        // $rootScope.isHomePage = toState.isHomePage;

        if (modifiedPrams) {
            return transition.router.stateService.target(toState, modifiedPrams);
        }
        // Fetch config object according to state's configId
        const toComponentName = proNavigator.getConfigIdFromStateConfig(toState, toParams);
        if (!toComponentName) {
            // cancel transition
            return false;
        }
        const toComponent = swSettings.components[toComponentName];
        const homeComponent = swSettings.components.Home;

        // Apply parameter reset logic if configured for state transitions
        try {
            const resetParams = applyResetParams(fromState, toState, fromParams, toParams);
            if (!areParamsEqual(resetParams, toParams)) {
                return transition.router.stateService.target(toState, resetParams);
            }
        } catch (error) {
            Sentry.error("Failed to apply reset parameters during state transition", error as Error, {
                fromState: fromState?.name,
                toState: toState.name,
            });
        }

        // Queries limiter
        const refreshOrNewTab = !!fromState.name;
        const hasRouteChanged = toState.name !== fromState.name || !areParamsEqual(fromParams, toParams);

        if (isQueryLimitReached) {
            if (isSIUser) {
                if (SI_ANALYSIS_TAB_ROUTES_SET.has(toState.name) && !isDataCreditsEnabled) {
                    if (refreshOrNewTab && hasRouteChanged) {
                        RecentService.getQueryLimiterHook();
                        return false;
                    }
                    if (fromState.name === "") {
                        return transition.router.stateService.target(COMPANY_ANALYSIS_HOME);
                    }
                }
            } else {
                if (refreshOrNewTab && hasRouteChanged) {
                    RecentService.getQueryLimiterHook();
                    return false;
                }
            }
        }
        // end of Queries limiter

        // redirect on failed payment
        if (
            homeComponent.resources.IsPaymentRequired &&
            homeComponent.resources.IsGracePeriodPassed &&
            !toState.name.match(/account/)
        ) {
            window.location.href = `${swSettings.swsites.login}/billing-information`;
            return false;
        }

        const navigate = canNavigate(toState, toParams);
        const connectedAccountState = swConnectedAccountsService.getUiOptions(toState);

        if (!navigate && !connectedAccountState.showAvailableAppsIntro) {
            openStateUnlockModal(toState, toParams);

            if (fromState.name === "") {
                const nextState = isSIUser
                    ? proNavigator.getState(HOME_PAGE_ROUTE)
                    : getActiveSibling(toState, toParams) || getHomePage(toState, toParams);
                return transition.router.stateService.target(nextState, toParams);
            }

            if (!connectedAccountState.IsAvailableOnPage) {
                return false;
            }
        }

        const isAppsState =
            (toState.name.match(/apps-/) && !toState.name.match(/home/)) ||
            (toState.name.match(/_app_/) && !toState.name.match(/home/));
        const isAppCategoryAnalysisState =
            (toState.name.match(/appcategory-/) && !toState.name.match(/home/)) ||
            (toState.name.match(/_appmarketanalysis/) && !toState.name.match(/home/));
        const isDashboardState = toState.name.indexOf("dashboard") === 0;
        const isLeadGeneratorState = toState.name.indexOf("leadGenerator") === 0;
        const isWebsiteState =
            toState.name.match(/websites-/) ||
            toState.name.match(/accountreview_website_/) ||
            toState.name.match(/competitiveanalysis_website_/) ||
            toState.name.match(/digitalsuite_website_/) ||
            toState.name.match(/digitalsuite_websites_/) ||
            toState.name.match(/organicsearch_website_/) ||
            toState.name.match(/paidsearch_website_/) ||
            toState.name.match(/search_website_/) ||
            toState.name.match(/digitalsuite_analyzepublishers_/) ||
            toState.name.match(/acquisition_display_/) ||
            toState.name.match(/digitalsuite_backlinks_/) ||
            toState.name.match(/organicsearch_websites_/) ||
            toState.name.startsWith("adintelligence") ||
            toState.name.startsWith("aitraffic") ||
            toState.name.startsWith("airesearch");
        const isIndustryAnaysisState =
            toState.name.match(/industryAnalysis-/) ||
            toState.name.match(/marketresearch_webmarketanalysis_mapping/) ||
            toState.name.match(/findkeywords_byindustry/) ||
            toState?.parent?.match(/marketresearch_webmarketanalysis/) ||
            toState?.parent?.match(/market_research_pages_abstract_parent/) ||
            toState?.parent?.match(/marketresearch_keywordmarketanalysis_search/);
        const isKeywordsAnalysisState =
            toState.name.match(/keywordAnalysis-(?!home)/) ||
            toState.name.match(/keywordAnalysis_/) ||
            toState.name.match(/marketresearch_keywordmarketanalysis/) ||
            toState.name.match(/monitorkeywords/) ||
            toState.name.match(/digitalsuite_findkeywords_keywordGeneratorTool/);
        const isWebsiteReferralState = toState.name === "digitalsuite_website_referrals_incomingtraffic"; // check with ori
        const isConversionSegmentModuleState = toState.name.match(/conversion-customsegement/);
        const isSegmentState = toState.parent === SEGMENTS_ANALYSIS_ABSTRACT_PARENT_ID;
        const isAdIntelligenceState = toState.name.match(/^adintelligence/);

        const hasLicense = swSettings.components.Home.resources.HasLicense;
        if (!hasLicense && !isDashboardState) {
            return transition.router.stateService.target("dashboard");
        }

        if (isAppsState) {
            unsupportedDuration = !swSettings.allowedDuration(toParams.duration, toComponentName);
            unsupportedCountry = !swSettings.allowedCountry(toParams.country, toComponentName);
            const hasPremiumPlus = getHasPremiumPlusClaim();

            if ((unsupportedDuration || unsupportedCountry) && !hasPremiumPlus) {
                return transition.router.stateService.target(
                    toState,
                    Object.assign(
                        {},
                        toParams,
                        unsupportedDuration
                            ? {
                                  duration: toComponent.defaultParams.duration,
                              }
                            : {},
                        unsupportedCountry
                            ? {
                                  country: toComponent.defaultParams.country,
                              }
                            : {},
                        toState.defaultQueryParams,
                    ),
                );
            }
            // Clear universal search on mobile apps
            if (toState.clearSearch) {
                chosenSites.clear(toParams);
            }
        }
        if (isAppCategoryAnalysisState) {
            /*
             * fix SIM-39871: In case the countries parameter inside the component config is empty
             * we will redirect the user using the default country, even if its not part of the countries
             * list which is empty (this is a public data, so we are allowed to present it)
             * */
            const isAllowedCountry = swSettings.allowedCountry(toParams.country, toComponentName);
            if (toParams.country !== toComponent.defaultParams.country && !isAllowedCountry) {
                const country =
                    toComponent.allowedCountries.length > 0
                        ? toComponent.allowedCountries[0]?.id
                        : toComponent.defaultParams.country;
                newParams = {
                    ...toParams,
                    country,
                };
                return transition.router.stateService.target(toState, newParams);
            }
        }
        if (isDashboardState) {
            const userHasDashboards = DashboardService.dashboards.length > 0;
            const readonlyDashboardUser = swSettings.components.Dashboard.resources.IsReadonly;
            const isCommonAccount = !swSettings.components.Dashboard.resources.CanViewDashboard;

            switch (toState.name) {
                case "dashboard":
                    const sharedDashboards = !isCommonAccount
                        ? await ShareDashboardService.getSharedWithMe().then(({ dashboards }) => {
                              return dashboards;
                          })
                        : [];
                    const userHasSharedDashboards = (sharedDashboards?.length ?? 0) > 0;
                    if (userHasDashboards) {
                        return transition.router.stateService.target("dashboard-exist", {
                            dashboardId: DashboardService.getFirstDashboard().id,
                        });
                    } else if (readonlyDashboardUser) {
                        openStateUnlockModal(toState);
                        return transition.router.stateService.target("trackHomepage", toParams);
                    } else if (!hasLicense) {
                        if (!userHasSharedDashboards) {
                            return transition.router.stateService.target("trackHomepage", toParams);
                        } else {
                            return transition.router.stateService.target("dashboard-exist", {
                                // @ts-ignore FIXME: strict migration - 'sharedDashboards' is possibly 'undefined' — guarded by userHasSharedDashboards (length > 0) which implies the array exists
                                dashboardId: sharedDashboards[0].id,
                            });
                        }
                    } else {
                        return transition.router.stateService.target("dashboard-gallery");
                    }
                case "dashboard-new":
                case "dashboard-gallery":
                    if (readonlyDashboardUser) {
                        openStateUnlockModal(toState);
                        return transition.router.stateService.target("trackHomepage", toParams);
                    } else if (!hasLicense) {
                        return transition.router.stateService.target("trackHomepage", toParams);
                    }
                    break;
                default:
                    break;
            }
        }

        if (isLeadGeneratorState) {
            if (toState.name === "leadGenerator") {
                return transition.router.stateService.target("leadGenerator.all");
            }
        }
        if (isWebsiteState) {
            // test if key is valid website
            const targetState = validateWebsiteKey(toParams, toState, transition);
            if (targetState) {
                return targetState;
            }

            // check if route is supported
            const isNotVirtualSupported =
                // @ts-ignore FIXME: strict migration - Object is possibly 'undefined' — chosenSites.getPrimarySite() returns IPrimarySite | undefined; legacy callsite assumes a primary site exists in website states
                chosenSites.getPrimarySite().isVirtual && toState.isVirtualSupported === false;
            const isPeriodOverPeriod = toState.periodOverPeriodEnabled;
            if (isNotVirtualSupported) {
                return transition.router.stateService.target("digitalsuite_website_trafficandengagement", toParams);
            } else if (isPeriodOverPeriod && toParams.comparedDuration) {
                const isPeriodOverPeriodAllowed = periodOverPeriodService.periodOverPeriodEnabled(
                    toParams.duration,
                    toParams.comparedDuration,
                    toParams.key,
                    toComponentName ?? "",
                );
                // don't allow compare to previous year
                if (!isPeriodOverPeriodAllowed) {
                    newParams = clone(toParams);
                    newParams.comparedDuration = undefined;
                    return transition.router.stateService.target(toState, newParams);
                }
            }

            const webSourceTargetState = webSourceNavChangeHandler(transition);
            if (webSourceTargetState) {
                return webSourceTargetState;
            }

            // Redirect logic, according to component settings
            unsupportedDuration =
                !toState.skipDurationCheck && !swSettings.allowedDuration(toParams.duration, toComponentName, toState);
            unsupportedCountry =
                !toState.skipCountryCheck && !swSettings.allowedCountry(toParams.country, toComponentName);
            unsupportedDomain = isSubdomainsFilterDisabled(toParams, toState) && toParams.isWWW === "-";

            const isState =
                CountryService.countriesById[toParams.country] &&
                !!CountryService.countriesById[toParams.country].parent;
            const isNotStatesSupported = isState && !toState.isUSStatesSupported;
            // checking whether the country is valid for the module but not for the page
            if ((unsupportedCountry && !toState.worldwideOnly) || isNotStatesSupported) {
                if (
                    toParams.key &&
                    find(swSettings.components.WebAnalysis.allowedCountries, {
                        id: Number(toParams.country),
                    })
                ) {
                    return;
                }
            }
            // if the page won't have the calendar we don't want to allow custom duration SIM-32894
            if (
                !unsupportedDuration &&
                !isCustomDurationAllowed(toState.hideCalendar) &&
                swSettings.isCustomDuration(toParams.duration)
            ) {
                unsupportedDuration = true;
            }
            if (unsupportedDuration && toParams.webSource === "MobileWeb" && isWebsiteReferralState) {
                // When moving to websource = mobileWeb, if the duration isn't supported,
                // use the maximum available range
                toParams.duration = DurationService.getDiffCustomRangeParam(
                    swSettings.current.startDate,
                    swSettings.current.endDate,
                );
                return transition.router.stateService.target(toState, Object.assign({}, toParams));
            }
            if (unsupportedDuration || unsupportedCountry || unsupportedDomain) {
                const newToParams = { ...toParams };
                newToParams.duration = unsupportedDuration
                    ? resolveFallbackAllowedDuration(toComponentName, toState)
                    : toParams.duration;
                newToParams.country = unsupportedCountry ? swSettings.current.defaultParams.country : toParams.country;
                newToParams.isWWW = unsupportedDomain ? "*" : (toParams.isWWW ?? "*");
                return transition.router.stateService.target(toState, newToParams);
            }

            if (
                ["digitalsuite_website_marketingchannels", "accountreview_website_marketingchannels"].includes(
                    toState.name,
                )
            ) {
                //MobileWeb doesnt support those params, resetting.
                if (toParams.webSource === "MobileWeb") {
                    delete toParams.channelAnalysisMetric;
                    delete toParams.channelAnalysisGranularity;
                    delete toParams.channelAnalysisChannel;
                    delete toParams.channelAnalysisMtd;
                }
            }

            let icon;
            let image;
            if (toParams.key in chosenSites.listInfo) {
                const siteInfo = chosenSites.listInfo[toParams.key];
                icon = siteInfo.icon;
                image = siteInfo.image;
            } else {
                image = icon = AssetsService.assetUrl("/images/bg_c.png");
            }

            // $rootScope.global.site.icon = icon;
            // $rootScope.global.site.image = image;

            // update title
            // TODO: RIPNG - how we can know the sites's name?
            // document.title = "SimilarWeb PRO - " + $rootScope.global.site.name;

            // Clear universal search on website analysis
            if (toState.clearSearch) {
                chosenItems.$clear();
            }
        }
        if (isIndustryAnaysisState) {
            unSupportedOldCategoryNewMapping = OLD_TO_NEW_CATEGORIES_MAP[toParams.category];
            if (unSupportedOldCategoryNewMapping) {
                return transition.router.stateService.target(
                    toState,
                    merge(toParams, {
                        category: unSupportedOldCategoryNewMapping,
                    }),
                );
            }
            unsupportedCountry =
                toState.name === "digitalsuite_marketresearch_webmarketanalysis_geography"
                    ? false
                    : !swSettings.allowedCountry(toParams.country, toComponentName);
            unsupportedDuration =
                !toState.skipDurationCheck && !swSettings.allowedDuration(toParams.duration, toComponentName, toState);
            if (unsupportedCountry) {
                // TODO: RIPNG - we need to support this, But it should be in the module container, not here
                // $rootScope.showCountryTooltip = true;
                setTimeout(() => {
                    store.dispatch(onUnsupportedCountryRedirect());
                }, 100);
                return transition.router.stateService.target(toState, {
                    ...toParams,
                    country: toComponent.defaultParams.country,
                });
            } else if (unsupportedDuration) {
                return transition.router.stateService.target(toState, {
                    ...toParams,
                    duration: toComponent.defaultParams.duration,
                });
            }
            if (toParams.comparedDuration) {
                const isPeriodOverPeriodAllowed = periodOverPeriodService._periodOverPeriodEnabled(
                    toParams.duration,
                    toParams.comparedDuration,
                    [],
                    toComponentName,
                );
                if (!isPeriodOverPeriodAllowed) {
                    return transition.router.stateService.target(toState, {
                        ...toParams,
                        comparedDuration: undefined,
                    });
                }
            }

            const webSourceTargetState = webSourceNavChangeHandler(transition);
            if (webSourceTargetState) {
                return webSourceTargetState;
            }
        }
        if (isKeywordsAnalysisState) {
            unsupportedDuration =
                !toState.skipDurationCheck &&
                toParams.duration &&
                !swSettings.allowedDuration(toParams.duration, toComponentName, toState);
            unsupportedCountry =
                !toState.skipCountryCheck && !swSettings.allowedCountry(toParams.country, toComponentName);
            if (unsupportedCountry) {
                return transition.router.stateService.target(toState.name, {
                    ...toParams,
                    country: swSettings.current.resources.InitialCountry,
                });
            }
            if (unsupportedDuration) {
                return transition.router.stateService.target(toState, {
                    ...toParams,
                    duration: toComponent.defaultParams.duration,
                });
                // seems no in use
            }

            const webSourceTargetState = webSourceNavChangeHandler(transition);
            if (webSourceTargetState) {
                return webSourceTargetState;
            }
        }
        if (isConversionSegmentModuleState) {
            const isPeriodOverPeriodAllowed = periodOverPeriodService._periodOverPeriodEnabled(
                toParams.duration,
                toParams.comparedDuration,
                [],
                toComponentName ?? "",
            );

            // don't allow compare to previous year
            if (toParams.comparedDuration && !isPeriodOverPeriodAllowed) {
                newParams = clone(toParams);
                newParams.comparedDuration = undefined;
                return transition.router.stateService.target(toState, newParams);
            }
        }
        if (isSegmentState) {
            const webSourceTargetState = webSourceNavChangeHandler(transition);
            if (webSourceTargetState) {
                return webSourceTargetState;
            }
        }
        if (isAdIntelligenceState) {
            const webSourceTargetState = webSourceNavChangeHandler(transition);
            if (webSourceTargetState) {
                return webSourceTargetState;
            }

            // Redirect logic, according to component settings
            unsupportedDuration =
                !toState.skipDurationCheck && !swSettings.allowedDuration(toParams.duration, toComponentName, toState);
            unsupportedCountry =
                !toState.skipCountryCheck && !swSettings.allowedCountry(toParams.country, toComponentName);

            if (
                !unsupportedDuration &&
                !isCustomDurationAllowed(toState.hideCalendar) &&
                swSettings.isCustomDuration(toParams.duration)
            ) {
                unsupportedDuration = true;
            }

            if (unsupportedDuration || unsupportedCountry) {
                const newToParams = { ...toParams };
                newToParams.duration = unsupportedDuration
                    ? resolveFallbackAllowedDuration(toComponentName, toState)
                    : toParams.duration;
                newToParams.country = unsupportedCountry ? swSettings.current.defaultParams.country : toParams.country;
                return transition.router.stateService.target(toState, newToParams);
            }
        }
    });
};
